// React Imports
import React, { useState, useRef } from "react";

// Material UI Imports
import { Box, Divider, Grid, Typography } from "@material-ui/core";
import { RotateLeft, SettingsBackupRestore } from "@material-ui/icons";
import SaveIcon from "@material-ui/icons/Save";

// Styles / Components
import Button from "components/CustomButtons/Button.js";
import MDEditor from "@uiw/react-md-editor";
import ExpandMore from "@material-ui/icons/ExpandMore";
import FeedbackSection from "./FeedbackSection";
import HeaderSection from "./HeaderSection";
import { v4 as uuidv4 } from 'uuid';
import { convertToPdf, regenerate } from "views/APIs/ToolboxUtility";
import { ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary } from "@material-ui/core";
import MarkdownEditorWithPreview from "../../../components/MarkdownEditor/MarkdownEditorWithPreview";

export default function ReviewScreen({ title, description, dropdown_label, dropdown_options, isMultiSelect, nextLabel, markdownContent, setMarkdownContent, onDropDownSelection, versionOptions,
    version, setVersion, onNext, onSave, setLoading, originalMarkdownContent
}) {
    const [feedback, setFeedback] = useState("");
    const [feedbackFiles, setFeedbackFiles] = useState([]);
    const [innerLoading, setInnerLoading] = useState(false);

    const onRegenerate = async () => {
        try {
            setInnerLoading(true);
            setLoading({
                show: true,
                type: "regenerate",
                message: "Regenerating the content for you. This might take a few seconds..."
            });
            const data = await regenerate({ markdownContent: markdownContent, feedback: feedback, files: feedbackFiles });
            setMarkdownContent(data);
            setFeedback("")
            setFeedbackFiles([])
            setLoading({
                show: false,
                type: "",
                message: ""
            });
        } catch (e) {
            setLoading({
                show: false,
                type: "",
                message: ""
            });
        }
        setInnerLoading(false);
    }

    const [downloadLoader, setDownloadLoader] = useState(false);

    const onDownload = () => {
        const handleDownload = async () => {
            const formData = new FormData();
            formData.append("markdown_content", markdownContent);
            formData.append("project_id", uuidv4());

            try {
                setDownloadLoader(true);
                const { blob, filename } = await convertToPdf(formData);
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);
            } catch (err) {
                console.error(err);
            }
            setDownloadLoader(false);
        };
        handleDownload();
    }

    return (
        <Box>
            {/* Header row */}
            <HeaderSection
                step_title={title}
                step_description={description}
                version={version}
                onVersionChange={setVersion}
                versionOptions={versionOptions}
                onDownload={onDownload}
                downloadLoader={downloadLoader}
                isMultiSelect={isMultiSelect}
                dropdownLabel={dropdown_label}
                dropdownOptions={dropdown_options}
                onSelectedOptionChange={onDropDownSelection}
            />

            <Divider style={{ marginTop: 10, marginBottom: 24 }} />

            {/* Editor */}
            <Grid container>
                <Grid item xs={12}>
                    <Box data-color-mode="light">
                        <MarkdownEditorWithPreview
                            value={markdownContent}
                            height={400}
                            onChange={(value) => setMarkdownContent(value ?? "")}
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* have a button to the extreme right saying revert to original */}
            <Button style={{ float: "right" }} size="sm" onClick={() => setMarkdownContent(originalMarkdownContent)} disabled={markdownContent === originalMarkdownContent} color="quCreateColor">
                <SettingsBackupRestore style={{ height: 15, marginLeft: -5 }} />
                Revert
            </Button>

            {/* Feedback */}
            <ExpansionPanel style={{ marginTop: 50, border: '2px solid #20677B', borderRadius: 8 }}>
                <ExpansionPanelSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" style={{ textTransform: "none", fontWeight: "bold", background: "linear-gradient(to right, #20677B, #1B4F72)", WebkitBackgroundClip: "text", color: "transparent" }}>˙⟡</Typography>
                    <Typography variant="h6" style={{ textTransform: "none" }}>&nbsp;Regenerate using&nbsp;</Typography>
                    <Typography variant="h6" style={{ textTransform: "none", fontWeight: "bold", background: "linear-gradient(to right, #20677B, #1B4F72)", WebkitBackgroundClip: "text", color: "transparent" }}>AI</Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <Grid container>
                        <Grid item xs={12}>
                            <FeedbackSection
                                onChange={setFeedback}
                                onFilesChange={setFeedbackFiles}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button onClick={onRegenerate} color="success" startIcon={<RotateLeft />}>
                                Regenerate
                            </Button>
                        </Grid>
                    </Grid>
                </ExpansionPanelDetails>
            </ExpansionPanel>

            {/* Footer actions */}
            <Grid container style={{ marginTop: 10 }}>
                <Grid item xs={12}>
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        style={{ gap: 10 }}
                        flexWrap="wrap"
                    >

                        <Box display="flex" style={{ gap: 10 }} alignItems="center">
                            <Button onClick={onSave} color="quCreateColor">
                                <SaveIcon /> &nbsp;Save Changes
                            </Button>

                            <Button
                                onClick={onNext}
                                color="quCreateColor"
                            >
                                {nextLabel}
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
